/**
 * PackageCountSheet — counting an item in the packages it is actually on the
 * shelf in.
 *
 * A cook counting mayonnaise is not holding "10.125 gallons". They are holding
 * a full Costco tub, two Sysco cases, eight loose bottles and an open tub. The
 * single number the count line stores is a conversion they had to do in their
 * head, at speed, standing in a walk-in — and once it is stored, nobody can
 * check it or fix one part of it without recounting the lot.
 *
 * This sheet lets them enter the four things they are looking at and does the
 * conversion itself. The count line still stores one number; these entries sit
 * beside it as the working.
 *
 * What it will not do:
 *
 *   Add up an entry it cannot convert. A package with no confirmed size is
 *   moved to "Needs unit review" and left OUT of the total, with the shortfall
 *   stated. Counting it as zero would understate the shelf and overstate the
 *   next order, silently, which is the failure this whole feature exists to
 *   prevent.
 *
 *   Relabel a discrete inner count. On a bottle-counted item, half a case of
 *   24 bottles is entered as twelve bottles, which is the rung below. On a
 *   case-counted item, 0.5 case is itself a meaningful on-hand count. Neither
 *   form grants permission to place a fractional vendor order.
 *
 * The ladder comes from the server (one call per session) and each rung carries
 * how much one of it is worth, so typing recomputes locally — no round trip per
 * keystroke, and it keeps working with no signal. A measure in a unit the
 * browser has no factor for ("2.5 gal" on an item counted in fl oz) is the one
 * case that needs the server, and it is requested on demand.
 *
 * Props:
 *   open              - visible
 *   onClose           - () => void
 *   itemName          - heading
 *   countUnit         - the item's canonical count unit
 *   rungs             - server-built ladder for this item
 *   unresolvedOptions - vendor products whose size could not be determined
 *   entries           - existing rows (may be empty)
 *   onSave            - (entries, total) => void | Promise
 *   onResolveMeasure? - async ({ quantity, unit }) => number | null
 *   mobile?           - bottom sheet instead of a centred panel
 *   saving?           - disables the primary action
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  LADDER_LEVELS,
  MISSING,
  totalFromEntries,
  describeEntries,
  formatQuantity,
} from '../utils/countUnitModel.js';

const C = {
  ink: '#3E2723',
  body: '#5A4A42',
  muted: '#8B7B73',
  line: '#E8DDD0',
  hairline: '#F1EAE2',
  surface: '#FEFCFA',
  link: '#8A5A00',
  amberBg: '#FFF4E0',
  amberLine: '#E6B866',
  green: '#2E7D32',
  greenBg: '#E8F5E9',
};

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(62,39,35,0.45)', zIndex: 10060,
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
  },
  overlayCentered: { alignItems: 'center' },
  panel: (mobile) => ({
    background: C.surface,
    border: `1px solid ${C.line}`,
    borderRadius: mobile ? '16px 16px 0 0' : 14,
    borderBottom: mobile ? 'none' : `1px solid ${C.line}`,
    boxShadow: '0 -8px 30px rgba(0,0,0,0.25)',
    width: mobile ? '100%' : 460,
    maxWidth: mobile ? '100%' : '94vw',
    maxHeight: mobile ? '86vh' : '84vh',
    overflowY: 'auto',
    padding: mobile
      ? '14px 16px calc(14px + env(safe-area-inset-bottom, 0px))'
      : '16px 18px 14px',
    // Hosts vary — a count row may set nowrap on its subtree.
    whiteSpace: 'normal',
    lineHeight: 1.35,
    textAlign: 'left',
  }),
  handle: { width: 36, height: 4, borderRadius: 2, background: '#D9CEC2', margin: '0 auto 10px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 },
  title: { fontWeight: 700, fontSize: 14, color: C.ink, wordBreak: 'break-word' },
  subtitle: { fontSize: 11, color: C.muted, marginTop: 2 },
  close: {
    background: 'none', border: 'none', fontSize: 20, lineHeight: 1, cursor: 'pointer',
    color: C.muted, padding: '0 2px', flexShrink: 0, minWidth: 32, minHeight: 32,
  },
  sectionLabel: {
    fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em',
    color: C.muted, margin: '10px 0 5px',
  },
  row: {
    display: 'grid',
    // max-content for the stepper column so a long vendor label never squeezes
    // the input to nothing, minmax(0,1fr) so it can shrink instead of overflow.
    gridTemplateColumns: 'minmax(0, 1fr) max-content',
    gap: 8,
    alignItems: 'center',
    padding: '7px 0',
    borderTop: `1px solid ${C.hairline}`,
  },
  rowText: { minWidth: 0 },
  rungLabel: { fontSize: 12.5, fontWeight: 700, color: C.ink, wordBreak: 'break-word' },
  rungMeta: { fontSize: 11, color: C.muted, marginTop: 1, wordBreak: 'break-word' },
  qtyInput: {
    width: 74, textAlign: 'center', fontSize: 15, fontWeight: 700, padding: '6px 4px',
    border: `2px solid ${C.line}`, borderRadius: 9, color: C.ink, background: '#fff',
    minHeight: 40, fontFamily: 'inherit',
  },
  unitPicker: {
    fontSize: 11.5, padding: '5px 6px', border: `1px solid ${C.line}`, borderRadius: 8,
    color: C.body, background: '#fff', fontFamily: 'inherit', maxWidth: 108,
  },
  measureRow: { display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' },
  total: {
    marginTop: 12, padding: '10px 12px', borderRadius: 10,
    background: C.greenBg, border: `1px solid #C8E6C9`,
  },
  totalUnknown: { background: C.amberBg, border: `1px solid ${C.amberLine}` },
  totalLabel: {
    fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', color: C.muted,
  },
  totalValue: { fontSize: 20, fontWeight: 900, color: C.ink, lineHeight: 1.2 },
  totalWorking: { fontSize: 11.5, color: C.body, marginTop: 3, wordBreak: 'break-word' },
  warn: {
    fontSize: 11.5, color: '#8A5A00', background: C.amberBg, border: `1px solid ${C.amberLine}`,
    borderRadius: 9, padding: '8px 10px', marginTop: 10, lineHeight: 1.4,
  },
  reviewItem: { fontSize: 11.5, color: C.body, marginTop: 4 },
  actions: { display: 'flex', gap: 8, marginTop: 14 },
  primary: {
    flex: 1, background: C.link, color: '#fff', border: 'none', borderRadius: 10,
    padding: '10px 12px', fontWeight: 700, fontSize: 13, cursor: 'pointer', minHeight: 44,
    fontFamily: 'inherit',
  },
  secondary: {
    background: '#fff', color: C.body, border: `1px solid ${C.line}`, borderRadius: 10,
    padding: '10px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer', minHeight: 44,
    fontFamily: 'inherit',
  },
};

/** A stable key for a rung so React does not remount inputs while typing. */
const rungKey = (rung) => rung.key || `${rung.optionId ?? 'canonical'}:${rung.level}`;

/** Seeds the sheet's per-rung quantities from any entries already stored. */
function seedQuantities(entries, rungs) {
  const seeded = {};
  (entries || []).forEach((entry) => {
    const match = (rungs || []).find((rung) => {
      if (entry.purchase_option_id == null) return rung.level === LADDER_LEVELS.CANONICAL;
      const wanted =
        entry.entry_kind === 'loose_inner_pack' ? LADDER_LEVELS.INNER_PACK : LADDER_LEVELS.PACKAGE;
      return String(rung.optionId) === String(entry.purchase_option_id) && rung.level === wanted;
    });
    if (!match) return;
    const key = rungKey(match);
    const previous = Number(seeded[key]) || 0;
    seeded[key] = String(previous + (Number(entry.entered_quantity) || 0));
  });
  return seeded;
}

/** Any stored entry that was a measure in some other unit, e.g. "2.5 gal". */
function seedMeasures(entries) {
  return (entries || [])
    .filter((e) => e.purchase_option_id == null && e.entered_unit && e.entry_kind === 'manual_measure')
    .map((e, index) => ({
      id: `measure-${index}`,
      quantity: String(e.entered_quantity ?? ''),
      unit: e.entered_unit || '',
      normalized: e.conversion_status === 'valid' ? e.normalized_quantity ?? null : null,
      pending: false,
      error: e.conversion_status === 'unresolved' ? 'This unit could not be converted.' : null,
    }));
}

export default function PackageCountSheet({
  open,
  onClose,
  itemName,
  countUnit,
  rungs = [],
  unresolvedOptions = [],
  entries = [],
  onSave,
  onResolveMeasure,
  mobile = false,
  saving = false,
}) {
  const [quantities, setQuantities] = useState({});
  const [measures, setMeasures] = useState([]);
  const panelRef = useRef(null);
  const measureSeq = useRef(0);

  // Reseed whenever the sheet opens on a different line, never mid-edit.
  useEffect(() => {
    if (!open) return;
    setQuantities(seedQuantities(entries, rungs));
    setMeasures(seedMeasures(entries));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, itemName]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const packageRungs = useMemo(
    () => rungs.filter((r) => r.level !== LADDER_LEVELS.CANONICAL),
    [rungs],
  );
  const canonicalRung = useMemo(
    () => rungs.find((r) => r.level === LADDER_LEVELS.CANONICAL) || null,
    [rungs],
  );

  // The draft entries, rebuilt from the inputs on every render. Cheap, and it
  // means the total on screen is always the total that will be saved.
  const draftEntries = useMemo(() => {
    const out = [];
    rungs.forEach((rung) => {
      const raw = quantities[rungKey(rung)];
      const qty = Number(raw);
      if (raw === '' || raw == null || !Number.isFinite(qty) || qty <= 0) return;
      out.push({
        purchase_option_id: rung.optionId ?? null,
        entry_kind:
          rung.level === LADDER_LEVELS.CANONICAL
            ? 'canonical_measure'
            : rung.level === LADDER_LEVELS.INNER_PACK
              ? 'loose_inner_pack'
              : 'full_package',
        entered_quantity: qty,
        entered_unit: rung.level === LADDER_LEVELS.CANONICAL ? countUnit : rung.unitLabel,
        sort_order: out.length,
      });
    });
    measures.forEach((measure) => {
      const qty = Number(measure.quantity);
      if (!Number.isFinite(qty) || qty <= 0 || !measure.unit) return;
      out.push({
        purchase_option_id: null,
        entry_kind: 'manual_measure',
        entered_quantity: qty,
        entered_unit: measure.unit,
        // A measure the server has already converted carries its answer, so
        // the local total includes it without another round trip.
        normalized_quantity: measure.normalized,
        conversion_status: measure.normalized == null ? 'unresolved' : 'valid',
        sort_order: out.length,
      });
    });
    return out;
  }, [rungs, quantities, measures, countUnit]);

  const result = useMemo(() => totalFromEntries(draftEntries, rungs), [draftEntries, rungs]);
  const working = describeEntries(result.resolved);
  const adjustments = result.warnings.filter((w) => !w.includes('not included in the total'));

  if (!open) return null;

  const setQty = (key, value) => {
    // Allow an empty field and a bare "." while typing; reject letters.
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    setQuantities((prev) => ({ ...prev, [key]: value }));
  };

  const addMeasure = () => {
    measureSeq.current += 1;
    setMeasures((prev) => [
      ...prev,
      { id: `measure-new-${measureSeq.current}`, quantity: '', unit: '', normalized: null, pending: false, error: null },
    ]);
  };

  const updateMeasure = (id, patch) =>
    setMeasures((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));

  const removeMeasure = (id) => setMeasures((prev) => prev.filter((m) => m.id !== id));

  /**
   * Converts one typed measure. The browser has no unit table, so this is the
   * one place the sheet needs the server; until it answers, the measure stays
   * out of the total rather than being guessed into it.
   */
  const resolveMeasure = async (measure) => {
    const qty = Number(measure.quantity);
    if (!Number.isFinite(qty) || qty <= 0 || !measure.unit) return;
    if (typeof onResolveMeasure !== 'function') {
      updateMeasure(measure.id, { error: 'This unit cannot be converted here.' });
      return;
    }
    updateMeasure(measure.id, { pending: true, error: null });
    try {
      const normalized = await onResolveMeasure({ quantity: qty, unit: measure.unit });
      updateMeasure(measure.id, {
        pending: false,
        normalized: normalized == null ? null : Number(normalized),
        error: normalized == null ? `${measure.unit} could not be converted to ${countUnit}.` : null,
      });
    } catch (error) {
      updateMeasure(measure.id, {
        pending: false,
        normalized: null,
        error: error?.message || 'That unit could not be converted.',
      });
    }
  };

  const handleSave = () => onSave?.(draftEntries, result.total);

  const body = (
    <div
      ref={panelRef}
      style={styles.panel(mobile)}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-label={`Count ${itemName || 'item'} by package`}
    >
      {mobile && <div style={styles.handle} />}

      <div style={styles.header}>
        <div style={{ minWidth: 0 }}>
          <div style={styles.title}>Count by package</div>
          <div style={styles.subtitle}>
            {itemName || 'Item'} · counted in {countUnit || 'unknown unit'}
          </div>
        </div>
        <button type="button" style={styles.close} onClick={onClose} aria-label="Close">×</button>
      </div>

      {packageRungs.length > 0 && (
        <>
          <div style={styles.sectionLabel}>Vendor packages</div>
          {packageRungs.map((rung) => (
            <div key={rungKey(rung)} style={styles.row}>
              <div style={styles.rowText}>
                <div style={styles.rungLabel}>{rung.label}</div>
                <div style={styles.rungMeta}>
                  {rung.detail ? `${rung.detail} · ` : ''}
                  one {rung.unitLabel} = {formatQuantity(rung.qtyInCountUnit, countUnit)}
                  {rung.allowsFractional ? ' · partials allowed' : ''}
                </div>
              </div>
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="0"
                aria-label={`${rung.label} count`}
                value={quantities[rungKey(rung)] ?? ''}
                onChange={(e) => setQty(rungKey(rung), e.target.value)}
                style={styles.qtyInput}
              />
            </div>
          ))}
        </>
      )}

      {canonicalRung && (
        <>
          <div style={styles.sectionLabel}>Loose {countUnit}</div>
          <div style={styles.row}>
            <div style={styles.rowText}>
              <div style={styles.rungLabel}>Measured directly</div>
              <div style={styles.rungMeta}>Anything already in {countUnit}</div>
            </div>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="0"
              aria-label={`Loose ${countUnit}`}
              value={quantities[rungKey(canonicalRung)] ?? ''}
              onChange={(e) => setQty(rungKey(canonicalRung), e.target.value)}
              style={styles.qtyInput}
            />
          </div>
        </>
      )}

      <div style={styles.sectionLabel}>Another unit</div>
      {measures.map((measure) => (
        <div key={measure.id} style={{ ...styles.row, gridTemplateColumns: '1fr' }}>
          <div style={styles.measureRow}>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="0"
              aria-label="Quantity"
              value={measure.quantity}
              onChange={(e) => updateMeasure(measure.id, { quantity: e.target.value, normalized: null })}
              onBlur={() => resolveMeasure(measure)}
              style={{ ...styles.qtyInput, width: 68 }}
            />
            <input
              type="text"
              autoComplete="off"
              placeholder="unit"
              aria-label="Unit"
              value={measure.unit}
              onChange={(e) => updateMeasure(measure.id, { unit: e.target.value, normalized: null })}
              onBlur={() => resolveMeasure(measure)}
              style={{ ...styles.unitPicker, width: 84 }}
            />
            <span style={styles.rungMeta}>
              {measure.pending
                ? 'converting…'
                : measure.normalized != null
                  ? `= ${formatQuantity(measure.normalized, countUnit)}`
                  : MISSING}
            </span>
            <button
              type="button"
              onClick={() => removeMeasure(measure.id)}
              style={{ ...styles.close, fontSize: 15, minWidth: 26, minHeight: 26 }}
              aria-label="Remove this measure"
            >
              ×
            </button>
          </div>
          {measure.error && <div style={{ ...styles.rungMeta, color: '#8A5A00' }}>{measure.error}</div>}
        </div>
      ))}
      <button type="button" onClick={addMeasure} style={{ ...styles.secondary, marginTop: 6, padding: '6px 10px', minHeight: 34 }}>
        + Add a measure
      </button>

      <div style={{ ...styles.total, ...(result.total == null ? styles.totalUnknown : null) }}>
        <div style={styles.totalLabel}>On hand</div>
        <div style={styles.totalValue}>
          {result.total == null ? MISSING : formatQuantity(result.total, countUnit)}
        </div>
        {working && <div style={styles.totalWorking}>{working}</div>}
        {result.total == null && (
          <div style={styles.totalWorking}>
            Nothing here converts yet, so there is no total to save.
          </div>
        )}
      </div>

      {/* Anything the total quietly did to a typed number — a fractional case
          rounded down to whole cases, say — has to be visible. The whole point
          is that the counter can check the arithmetic. The "n entries are not
          included" line is skipped here because the review block below states
          the same thing per entry. */}
      {adjustments.length > 0 && (
        <div style={styles.warn}>
          {adjustments.map((warning) => (
            <div key={warning} style={styles.reviewItem}>{warning}</div>
          ))}
        </div>
      )}

      {(result.hasUnresolved || unresolvedOptions.length > 0) && (
        <div style={styles.warn}>
          <strong>Needs unit review</strong>
          {result.unresolved.map((entry, index) => (
            <div key={`${entry.purchase_option_id ?? 'plain'}-${index}`} style={styles.reviewItem}>
              {entry.entered_quantity} {entry.entered_unit || countUnit} — {entry.reasonText} Not
              included in the total above.
            </div>
          ))}
          {unresolvedOptions.map((option) => (
            <div key={String(option.optionId)} style={styles.reviewItem}>
              {option.vendorLabel || 'A vendor product'}
              {option.packDescription ? ` (${option.packDescription})` : ''} has no confirmed size,
              so it cannot be counted by the package yet.
            </div>
          ))}
        </div>
      )}

      <div style={styles.actions}>
        <button type="button" style={styles.secondary} onClick={onClose}>Cancel</button>
        <button
          type="button"
          style={{ ...styles.primary, opacity: saving ? 0.6 : 1 }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save count'}
        </button>
      </div>
    </div>
  );

  return createPortal(
    <div
      style={{ ...styles.overlay, ...(mobile ? null : styles.overlayCentered) }}
      onClick={onClose}
      data-package-count-sheet="1"
    >
      {body}
    </div>,
    document.body,
  );
}
